import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { 
  Building2, Users, FileText, Home, DollarSign, AlertTriangle, 
  Calendar, Wrench, ArrowUpRight, ArrowDownRight 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const currency = language === 'tr' ? '₺' : '$';
  const welcomeText = language === 'tr' ? 'Hoş geldiniz' : 'Welcome back';

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent animate-spin"></div>
      </div>
    );
  }

  const unitStats = stats?.units || { total: 0, occupied: 0, vacant: 0, reserved: 0, maintenance: 0 };
  const financial = stats?.financial || { monthly_revenue: 0, outstanding_debt: 0, overdue_count: 0 };

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Welcome Header */}
      <div className="border-b border-gray-200 pb-6">
        <h1 className="font-heading text-4xl font-black tracking-tighter">
          {t('dashboard')}
        </h1>
        <p className="text-gray-500 mt-1">
          {welcomeText}, {user?.name}
        </p>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Units */}
        <div className="bg-white border border-black p-6 card-hover" data-testid="stat-total-units">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.1em] text-gray-500 mb-2">
                {t('totalUnits')}
              </p>
              <p className="metric-ban">{unitStats.total}</p>
            </div>
            <div className="p-2 bg-gray-100">
              <Building2 className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-4 text-sm">
            <span className="text-emerald-600">{unitStats.occupied} {t('occupied')}</span>
            <span className="text-gray-400">|</span>
            <span className="text-blue-600">{unitStats.vacant} {t('vacant')}</span>
          </div>
        </div>

        {/* Monthly Revenue */}
        <div className="bg-white border border-black p-6 card-hover" data-testid="stat-monthly-revenue">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.1em] text-gray-500 mb-2">
                {t('monthlyRevenue')}
              </p>
              <p className="metric-ban">{currency}{financial.monthly_revenue.toLocaleString()}</p>
            </div>
            <div className="p-2 bg-emerald-100">
              <DollarSign className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm text-emerald-600">
            <ArrowUpRight className="w-4 h-4" />
            <span>{t('thisMonth')}</span>
          </div>
        </div>

        {/* Outstanding Debt */}
        <div className="bg-white border border-black p-6 card-hover" data-testid="stat-outstanding-debt">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.1em] text-gray-500 mb-2">
                {t('outstandingDebt')}
              </p>
              <p className="metric-ban text-red-600">{currency}{financial.outstanding_debt.toLocaleString()}</p>
            </div>
            <div className="p-2 bg-red-100">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm text-red-600">
            <span>{financial.overdue_count} {t('overduePayments')}</span>
          </div>
        </div>

        {/* Contracts Ending */}
        <div className="bg-white border border-black p-6 card-hover" data-testid="stat-contracts-ending">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.1em] text-gray-500 mb-2">
                {t('contractsEndingSoon')}
              </p>
              <p className="metric-ban">{stats?.contracts_ending_soon || 0}</p>
            </div>
            <div className="p-2 bg-amber-100">
              <Calendar className="w-5 h-5 text-amber-600" />
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            {t('next30Days')}
          </div>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Unit Status Breakdown */}
        <div className="bg-white border border-gray-200 p-6">
          <h3 className="text-sm font-semibold mb-4">{t('units')} {language === 'tr' ? 'Durumu' : 'Status'}</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm">
                <span className="w-3 h-3 bg-emerald-500"></span>
                {t('occupied')}
              </span>
              <span className="font-mono text-sm">{unitStats.occupied}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm">
                <span className="w-3 h-3 bg-blue-500"></span>
                {t('vacant')}
              </span>
              <span className="font-mono text-sm">{unitStats.vacant}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm">
                <span className="w-3 h-3 bg-amber-500"></span>
                {t('reserved')}
              </span>
              <span className="font-mono text-sm">{unitStats.reserved}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm">
                <span className="w-3 h-3 bg-orange-500"></span>
                {t('inMaintenance')}
              </span>
              <span className="font-mono text-sm">{unitStats.maintenance}</span>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white border border-gray-200 p-6">
          <h3 className="text-sm font-semibold mb-4">{language === 'tr' ? 'Hızlı İstatistikler' : 'Quick Stats'}</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-gray-400" />
                {t('upcomingCheckIns')}
              </span>
              <span className="font-mono text-sm">{stats?.upcoming_checkins || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm">
                <Wrench className="w-4 h-4 text-gray-400" />
                {t('openRequests')}
              </span>
              <span className="font-mono text-sm">{stats?.open_maintenance_requests || 0}</span>
            </div>
          </div>
        </div>

        {/* Recent Payments */}
        <div className="bg-white border border-gray-200 p-6">
          <h3 className="text-sm font-semibold mb-4">{t('recentPayments')}</h3>
          <div className="space-y-3">
            {stats?.recent_payments?.length > 0 ? (
              stats.recent_payments.slice(0, 4).map((payment, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 truncate max-w-[150px]">
                    {payment.payment_date?.split('T')[0]}
                  </span>
                  <span className="font-mono text-emerald-600">
                    +{currency}{payment.amount?.toLocaleString()}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-400">{t('noData')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Occupancy Visual Bar */}
      <div className="bg-white border border-gray-200 p-6">
        <h3 className="text-sm font-semibold mb-4">{language === 'tr' ? 'Doluluk Oranı' : 'Occupancy Rate'}</h3>
        <div className="h-4 bg-gray-100 flex overflow-hidden">
          {unitStats.total > 0 && (
            <>
              <div 
                className="bg-emerald-500 h-full transition-all duration-500"
                style={{ width: `${(unitStats.occupied / unitStats.total) * 100}%` }}
              ></div>
              <div 
                className="bg-amber-500 h-full transition-all duration-500"
                style={{ width: `${(unitStats.reserved / unitStats.total) * 100}%` }}
              ></div>
              <div 
                className="bg-orange-500 h-full transition-all duration-500"
                style={{ width: `${(unitStats.maintenance / unitStats.total) * 100}%` }}
              ></div>
            </>
          )}
        </div>
        <div className="mt-3 flex items-center gap-6 text-xs">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-emerald-500"></span>
            {unitStats.total > 0 ? Math.round((unitStats.occupied / unitStats.total) * 100) : 0}% {t('occupied')}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-blue-500"></span>
            {unitStats.total > 0 ? Math.round((unitStats.vacant / unitStats.total) * 100) : 0}% {t('vacant')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
